import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCartItems, selectCartTotal, addToCart, removeFromCart, deleteFromCart, clearCart, fetchCart, updateQuantity } from './cartSlice';
import { Link, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { apiFetch } from '../../utils/api';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { getCustomerBaseUrl } from '../../utils/customerBase';
import { useGA4 } from '../../contexts/GA4Context';
import axios from 'axios';
import { isWhatsAppOrdersEnabled } from '../../utils/whatsappOrder';
import WhatsAppOrderModal from '../../components/WhatsAppOrderModal';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import { getStorefrontLineItemAvailability } from '../../utils/storefrontVariantUtils';
import '../../themes/edition/EditionCart.css';
import '../../themes/atelier/AtelierCart.css';
import '../../themes/gallery/GalleryCart.css';

export default function CartPage(){
  const items = useSelector(selectCartItems);
  const total = useSelector(selectCartTotal);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerId = useSelector(state => state.customer?.customer?._id);
  const { isAuthenticated: isCustomerLoggedIn } = useCustomerAuth();
  const token = null; // merchant app does not use customer token here
  const { trackViewCart, trackRemoveFromCart, trackBeginCheckout } = useGA4();
  const hasTrackedCart = useRef(false);

  // Get currency formatting from storefront context
  const { formatPrice, displayCurrency, storeCurrency } = useStorefrontCurrency();
  // Currency formatter helper that uses the context
  const currency = {
    format: (amount) => formatPrice(amount)
  };

  // Get store from StorefrontLayout context (same approach as ProductPage)
  const outletContext = useOutletContext() || {};
  const { store: outletStore = {}, premiumThemeConfig } = outletContext;
  const isEditionTheme = premiumThemeConfig?.theme?.slug === 'edition';
  const isAtelierTheme = premiumThemeConfig?.theme?.slug === 'atelier';
  const isGalleryTheme = premiumThemeConfig?.theme?.slug === 'gallery';

  // GA4: Track cart view when items are present
  useEffect(() => {
    if (items.length > 0 && !hasTrackedCart.current) {
      trackViewCart({
        currency: 'NGN',
        total: total,
        items: items.map(({ product, qty }) => ({
          productId: product._id,
          name: product.name,
          price: product.price,
          quantity: qty
        }))
      });
      hasTrackedCart.current = true;
    }
  }, [items, total, trackViewCart]);

  // Mobile optimization styles
  const mobileStyles = `
    @media (max-width: 768px) {
      .cart-main-grid {
        grid-template-columns: 1fr !important;
        gap: 24px !important;
      }

      .cart-page-container {
        padding: 16px !important;
      }

      .cart-item-card {
        flex-direction: column !important;
        align-items: flex-start !important;
        padding: 16px !important;
      }

      .cart-item-image {
        width: 100% !important;
        height: 200px !important;
        margin-bottom: 12px !important;
      }

      .cart-item-details {
        width: 100% !important;
      }

      .cart-quantity-controls {
        width: 100% !important;
        justify-content: space-between !important;
        margin: 12px 0 !important;
      }

      .cart-quantity-button {
        width: 44px !important;
        height: 44px !important;
        min-width: 44px !important;
      }

      .cart-item-total {
        width: 100% !important;
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
      }

      .cart-summary-card {
        position: static !important;
      }

      .checkout-button {
        width: 100% !important;
        min-height: 48px !important;
      }

      .continue-shopping-button {
        width: 100% !important;
        min-height: 44px !important;
      }

      .cart-actions-row {
        flex-direction: column !important;
        gap: 12px !important;
      }

      .cart-actions-row > * {
        flex: 1 1 100% !important;
      }
    }

    @media (max-width: 480px) {
      .cart-page-container {
        padding: 12px !important;
      }

      .cart-header-title {
        font-size: 28px !important;
      }

      .cart-item-card {
        padding: 12px !important;
      }

      .cart-item-image {
        height: 160px !important;
      }
    }
  `;

  // Store settings state - prefer outlet context store, fallback to fetched data
  const [fetchedStoreSettings, setFetchedStoreSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);

  // Use outlet context store if available (from StorefrontLayout), otherwise use fetched settings
  const storeSettings = outletStore?.slug ? outletStore : fetchedStoreSettings;

  // WhatsApp Order Modal state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

  // Get store slug - prefer outlet context, fallback to URL detection
  const storeSlug = useMemo(() => {
    // First priority: get from outlet context (same as ProductPage)
    if (outletStore?.slug) {
      return outletStore.slug;
    }

    // Fallback: try to extract from path-based routing (/s/slug)
    const path = window.location.pathname;
    const pathMatch = path.match(/\/s\/([^/]+)/);
    if (pathMatch) {
      return pathMatch[1];
    }

    // Fallback: try to extract from subdomain (slug.localhost:3000 or slug.domain.com)
    const hostname = window.location.hostname;
    const platformDomain = process.env.REACT_APP_PLATFORM_DOMAIN || 'localhost';
    const platformDomainWithoutPort = platformDomain.split(':')[0];
    const reservedSubdomains = ['admin', 'api', 'www', 'app', 'dashboard', 'cdn', 'static'];

    // Remove www. if present
    const cleanHostname = hostname.replace(/^www\./, '');

    // Check if this is a subdomain (not the base domain)
    if (cleanHostname.includes('.') && cleanHostname.endsWith(platformDomainWithoutPort)) {
      // Extract subdomain
      const subdomain = cleanHostname.substring(0, cleanHostname.length - platformDomainWithoutPort.length - 1);

      // Return subdomain if it's not reserved
      if (subdomain && !reservedSubdomains.includes(subdomain)) {
        return subdomain;
      }
    }

    return '';
  }, [outletStore?.slug]); // Re-run when outlet store changes

  // Fetch cart on page load for all users (both authenticated and guest)
  // The backend maintains cart in session cookies which persist across page refreshes
  useEffect(() => {
    if (storeSlug) {
      console.log('CartPage: Fetching cart from backend session for store:', storeSlug);
      dispatch(fetchCart(customerId));
    } else {
      console.warn('CartPage: No storeSlug found, cannot fetch cart');
    }
  }, [dispatch, customerId, storeSlug]);

  // Fetch store settings only if not available from outlet context
  useEffect(() => {
    const fetchStoreSettings = async () => {
      // Skip if we already have store from outlet context
      if (outletStore?.slug) {
        console.log('CartPage: Using store from outlet context:', outletStore.slug);
        return;
      }

      if (!storeSlug) return;

      try {
        setLoadingSettings(true);
        const response = await axios.get(`/api/stores/${storeSlug}`);
        if (response.data && response.data.store) {
          // Store the nested store object directly
          setFetchedStoreSettings(response.data.store);
          console.log('CartPage: Store settings loaded:', response.data.store);
        }
      } catch (error) {
        console.error('Error fetching store settings:', error);
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchStoreSettings();
  }, [storeSlug, outletStore?.slug]);

  // WhatsApp Order Handler - Opens the WhatsApp Order Modal
  const handleWhatsAppOrder = () => {
    if (!storeSettings || !isWhatsAppOrdersEnabled(storeSettings) || items.length === 0) return;
    setShowWhatsAppModal(true);
  };

  // Handle adding product from URL query parameter
  useEffect(() => {
    const productId = searchParams.get('product');
    if (productId && storeSlug) {
      const addProductFromUrl = async () => {
        try {
          console.log('CartPage: Adding product from URL:', productId);
          // Fetch product details
          const response = await axios.get(`/api/products/${productId}`);
          if (response.data && response.data.product) {
            const product = response.data.product;
            // Add product to cart
            dispatch(addToCart({ product, quantity: 1 }));
            console.log('CartPage: Product added to cart from URL');
          }
          // Remove product parameter from URL
          searchParams.delete('product');
          setSearchParams(searchParams, { replace: true });
        } catch (error) {
          console.error('Error adding product from URL:', error);
        }
      };

      addProductFromUrl();
    }
  }, [searchParams, storeSlug, dispatch, setSearchParams]);

  if (items.length === 0) {
    return (
      <>
        <style>{mobileStyles}</style>
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#F8FAFC',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
        <Card padding="xl" style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontSize: '64px', marginBottom: 'var(--space-4)' }}>🛒</div>
          <h2 style={{ 
            fontSize: 'var(--font-size-2xl)', 
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-gray-900)',
            margin: '0 0 var(--space-2) 0'
          }}>
            Your cart is empty
          </h2>
          <p style={{ 
            fontSize: 'var(--font-size-base)', 
            color: 'var(--color-gray-600)',
            margin: '0 0 var(--space-6) 0'
          }}>
            Add some products to get started
          </p>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Button variant="primary">
              Continue Shopping
            </Button>
          </Link>
        </Card>
      </div>
      </>
    );
  }

  return (
    <>
      <style>{mobileStyles}</style>
      <div className={isEditionTheme ? 'edition-cart' : isAtelierTheme ? 'atelier-cart' : isGalleryTheme ? 'gallery-cart' : ''} style={{
        minHeight: '100vh',
        background: '#F8FAFC',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: 'var(--space-6) 0'
      }}>
      <div className="cart-page-container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 var(--space-4)' }}>
        {/* Header */}
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <h1 className="cart-header-title" style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-gray-900)',
            margin: '0 0 var(--space-2) 0'
          }}>
            Shopping Cart
          </h1>
          <p style={{ 
            fontSize: 'var(--font-size-lg)', 
            color: 'var(--color-gray-600)',
            margin: 0
          }}>
            {items.length} item{items.length > 1 ? 's' : ''} in your cart
          </p>
        </div>

        <div className="cart-main-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-8)' }}>
          {/* Cart Items */}
          <div>
            <Card padding="lg">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {items.map((item) => {
                  const { product, qty } = item;
                  const availability = getStorefrontLineItemAvailability(product, item);
                  const maxQuantity = availability?.availableQuantity ?? product.stock ?? product.quantity ?? 99;
                  const itemKey = item.variantSku || item.variantId || product._id;

                  return (
                  <div key={itemKey} className="cart-item-card" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-4)',
                    padding: 'var(--space-4)',
                    border: '1px solid var(--color-gray-200)',
                    borderRadius: 'var(--radius-lg)',
                    background: 'var(--bg-primary)'
                  }}>
                    {/* Product Image */}
                    <div className="cart-item-image" style={{
                      width: '80px',
                      height: '80px',
                      borderRadius: 'var(--radius-md)',
                      background: 'var(--color-gray-100)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      flexShrink: 0
                    }}>
                      {product.imageUrl ? (
                        <img 
                          src={product.imageUrl} 
                          alt={product.name} 
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover' 
                          }} 
                        />
                      ) : (
                        <span style={{ color: 'var(--color-gray-400)', fontSize: '24px' }}>📦</span>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="cart-item-details" style={{ flex: 1 }}>
                      <h3 style={{ 
                        fontSize: 'var(--font-size-lg)', 
                        fontWeight: 'var(--font-weight-semibold)',
                        color: 'var(--color-gray-900)',
                        margin: '0 0 var(--space-1) 0'
                      }}>
                        {product.name}
                      </h3>
                      <p style={{ 
                        fontSize: 'var(--font-size-sm)', 
                        color: 'var(--color-gray-600)',
                        margin: '0 0 var(--space-2) 0'
                      }}>
                        {product.description?.substring(0, 100)}...
                      </p>
                      <p style={{ 
                        fontSize: 'var(--font-size-lg)', 
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-primary)',
                        margin: 0
                      }}>
                        {currency.format(product.price)}
                      </p>
                    </div>

                    {/* Quantity Controls */}
                    <div className="cart-quantity-controls" style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      marginRight: 'var(--space-4)'
                    }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => dispatch(updateQuantity({ productId: product._id, variantId: item.variantId, quantity: qty - 1 }))}
                        disabled={qty <= 1}
                        className="cart-quantity-button"
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          opacity: qty <= 1 ? 0.5 : 1,
                          cursor: qty <= 1 ? 'not-allowed' : 'pointer'
                        }}
                      >
                        -
                      </Button>
                      <span style={{
                        minWidth: '32px',
                        textAlign: 'center',
                        fontWeight: 'var(--font-weight-semibold)'
                      }}>
                        {qty}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => dispatch(updateQuantity({ productId: product._id, variantId: item.variantId, quantity: qty + 1 }))}
                        disabled={qty >= maxQuantity}
                        className="cart-quantity-button"
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          opacity: qty >= maxQuantity ? 0.5 : 1,
                          cursor: qty >= maxQuantity ? 'not-allowed' : 'pointer'
                        }}
                      >
                        +
                      </Button>
                    </div>

                    {/* Total Price */}
                    <div className="cart-item-total" style={{
                      textAlign: 'right',
                      minWidth: '120px'
                    }}>
                      <p style={{ 
                        fontSize: 'var(--font-size-lg)', 
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-gray-900)',
                        margin: '0 0 var(--space-1) 0'
                      }}>
                        {currency.format(product.price * qty)}
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => dispatch(removeFromCart({ productId: product._id, variantId: item.variantId, customerId }))}
                        style={{ color: 'var(--color-error)' }}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                );
                })}
              </div>
            </Card>
          </div>

          {/* Order Summary */}
          <div>
            <Card padding="lg" className="cart-summary-card" style={{ position: 'sticky', top: 'var(--space-6)' }}>
              <h3 style={{ 
                fontSize: 'var(--font-size-xl)', 
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-gray-900)',
                margin: '0 0 var(--space-4) 0'
              }}>
                Order Summary
              </h3>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: 'var(--space-2)'
              }}>
                <span style={{ color: 'var(--color-gray-600)' }}>Subtotal</span>
                <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                  {currency.format(total)}
                </span>
              </div>
              
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: 'var(--space-4)',
                paddingTop: 'var(--space-2)',
                borderTop: '1px solid var(--color-gray-200)'
              }}>
                <span style={{ 
                  fontSize: 'var(--font-size-lg)', 
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-gray-900)'
                }}>
                  Total
                </span>
                <span style={{ 
                  fontSize: 'var(--font-size-lg)', 
                  fontWeight: 'var(--font-weight-bold)',
                  color: 'var(--color-gray-900)'
                }}>
                  {currency.format(total)}
                </span>
              </div>

              {/* Checkout Actions - Minimalist Design */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>

                {/* Primary Action Row - Checkout + WhatsApp */}
                <div style={{ display: 'flex', gap: 'var(--space-3)', margin: '0 -20px' }}>
                  {/* Checkout Button - Full width primary CTA */}
                  <button
                    className="checkout-button"
                    onClick={async () => {
                      // Check if merchant requires account registration for checkout
                      if (storeSettings?.requireAccountRegistration && !isCustomerLoggedIn) {
                        const currentPath = window.location.pathname;
                        let redirectUrl;

                        if (currentPath.startsWith('/s/')) {
                          redirectUrl = `${window.location.origin}${currentPath.replace('/cart', '/checkout')}`;
                        } else {
                          redirectUrl = `${window.location.origin}/checkout`;
                        }

                        const base = getCustomerBaseUrl();
                        window.location.href = `${base}/login?redirect=${encodeURIComponent(redirectUrl)}&store=${storeSlug}`;
                        return;
                      }

                      let checkoutPath;
                      if (window.location.pathname.startsWith('/s/')) {
                        checkoutPath = window.location.pathname.replace('/cart', '/checkout');
                      } else {
                        checkoutPath = '/checkout';
                      }

                      // GA4: Track begin checkout
                      trackBeginCheckout({
                        currency: 'NGN',
                        total: total,
                        items: items.map(({ product, qty }) => ({
                          productId: product._id,
                          name: product.name,
                          price: product.price,
                          quantity: qty
                        }))
                      });

                      navigate(checkoutPath, { state: { storeSlug } });
                    }}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px 20px',
                      background: 'var(--color-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    Proceed to Checkout
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>

                  {/* WhatsApp Button */}
                  {storeSettings && isWhatsAppOrdersEnabled(storeSettings) && (
                    <button
                      onClick={handleWhatsAppOrder}
                      title="Order via WhatsApp"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '14px 16px',
                        background: '#25D366',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '0.9';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      WhatsApp
                    </button>
                  )}
                </div>

                {/* Secondary Actions - Text links */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 'var(--space-4)',
                  paddingTop: 'var(--space-1)'
                }}>
                  <Link
                    to="/"
                    style={{
                      textDecoration: 'none',
                      color: 'var(--color-gray-600)',
                      fontSize: '13px',
                      fontWeight: '500',
                      transition: 'color 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-primary)'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-gray-600)'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    Continue Shopping
                  </Link>

                  <span style={{ color: 'var(--color-gray-300)' }}>|</span>

                  <button
                    onClick={() => dispatch(clearCart())}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: 'var(--color-gray-500)',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'color 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#DC2626'}
                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-gray-500)'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    Clear Cart
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>

    {/* WhatsApp Order Modal */}
    <WhatsAppOrderModal
      isOpen={showWhatsAppModal}
      onClose={() => setShowWhatsAppModal(false)}
      items={items}
      storeSettings={storeSettings}
      storeSlug={storeSlug}
    />
    </>
  );
}
